import { Suspense, useState } from 'react';

import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { AppCacheProvider } from '@mui/material-nextjs/v15-pagesRouter';
import { BaseStyles, ThemeProvider, useCachedTheme } from '@kc-console/common-ui';
import { ModalProvider, NotificationProvider, ToastProvider } from '@kc-console/context';
import { createQueryClient } from '@kc-console/core/query';
import { ModalRenderer } from '@kc-console/layout';
import { FontFaceDefinitions } from '@kc-console/layout/fonts';

import ClientSideGuard from './ClientSideGuard';

import type { AppProps } from 'next/app';
import type { ReactNode } from 'react';

interface AppProvidersProps {
  appProps: AppProps;
  children: ReactNode;
  dehydratedState?: unknown;
}

export default function AppProviders({ children, dehydratedState, appProps }: AppProvidersProps) {
  const theme = useCachedTheme('light');
  const [queryClient] = useState(() => createQueryClient());

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={dehydratedState}>
        <AppCacheProvider {...appProps}>
          <ThemeProvider theme={theme}>
            <ToastProvider>
              <NotificationProvider>
                <ModalProvider>
                  <FontFaceDefinitions />
                  <BaseStyles />
                  <ClientSideGuard>
                    <Suspense>{children}</Suspense>
                  </ClientSideGuard>
                  <ModalRenderer />
                </ModalProvider>
              </NotificationProvider>
            </ToastProvider>
          </ThemeProvider>
        </AppCacheProvider>
      </HydrationBoundary>
    </QueryClientProvider>
  );
}
